/**
 * Phase 1b · Slice 2c — Lead Sources + Order Status panels + full
 * 10-export tab. All admin-only. Every panel keeps `unknown` visible.
 */
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatCents as fmt, UNKNOWN_LABEL } from "./adminSalesHelpers";

// ── Shared fetch hook ─────────────────────────────────────────────
function useSalesGet(path, deps) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get(path)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) setErr(formatApiError(e?.response?.data?.detail) || "Failed"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, err, loading };
}

// ── Lead Sources panel (first / last / customer-reported) ────────
export function LeadSourcesPanel({ preset, dateBasis }) {
  const [view, setView] = useState("first_touch");
  const { data, err, loading } = useSalesGet(
    `/admin/sales/lead-sources?preset=${preset}&date_basis=${dateBasis}&view=${view}`,
    [preset, dateBasis, view],
  );
  return (
    <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
             data-testid="lead-sources-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg">Lead Sources</h2>
        <div className="flex gap-2">
          {[["first_touch","First-touch"],["last_touch","Last-touch"],["customer_reported","Customer-reported"]].map(([v,l]) => (
            <button key={v}
                    data-testid={`lead-view-${v}`}
                    onClick={() => setView(v)}
                    className={`px-3 py-1 text-xs uppercase font-bold rounded-full border-2 border-[var(--ink)] ${
                      view === v ? "bg-[var(--ink)] text-white" : "bg-white"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className="text-[10px] uppercase text-[var(--smoke)] tracking-widest">
        Preview data · first-touch and last-touch reported separately · unknown preserved
      </div>
      {loading && <div className="text-sm">Loading…</div>}
      {err && <div className="text-sm text-red-700">{err}</div>}
      {data && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase text-[var(--smoke)]">
              <th className="pb-1">Source</th>
              <th className="pb-1 text-right">Orders</th>
              <th className="pb-1 text-right">Customer Total</th>
              <th className="pb-1 text-right">% Sales</th>
              <th className="pb-1 text-right">Refunds</th>
              <th className="pb-1 text-right">New</th>
              <th className="pb-1 text-right">Returning</th>
              <th className="pb-1 text-right">Guest</th>
              <th className="pb-1 text-right">Pickup / Ship</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.source_canonical}
                  data-testid={`lead-row-${r.source_canonical}`}
                  className="border-t border-neutral-100">
                <td className={`py-1 ${r.source_canonical === "unknown" ? "italic text-yellow-800" : ""}`}>
                  {r.source_canonical === "unknown" ? UNKNOWN_LABEL : r.source_canonical}
                </td>
                <td className="py-1 text-right font-mono">{r.orders}</td>
                <td className="py-1 text-right font-mono">{fmt(r.customer_total)}</td>
                <td className="py-1 text-right font-mono">{r.percentage_of_sales.toFixed(1)}%</td>
                <td className="py-1 text-right font-mono">{fmt(r.refund_total)}</td>
                <td className="py-1 text-right font-mono">{r.new_customers}</td>
                <td className="py-1 text-right font-mono">{r.returning_customers}</td>
                <td className="py-1 text-right font-mono">{r.guest_customers}</td>
                <td className="py-1 text-right font-mono">{r.local_pickup} / {r.delivery_shipping}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[var(--ink)]">
              <td className="py-1 font-bold text-xs">TOTAL</td>
              <td className="py-1 text-right font-mono">{data.totals.orders}</td>
              <td className="py-1 text-right font-mono">{fmt(data.totals.customer_total)}</td>
              <td colSpan={6} />
            </tr>
          </tfoot>
        </table>
      )}
    </section>
  );
}

// ── Order Status panel ───────────────────────────────────────────
export function OrderStatusPanel({ preset, dateBasis }) {
  const { data, err, loading } = useSalesGet(
    `/admin/sales/order-status-report?preset=${preset}&date_basis=${dateBasis}`,
    [preset, dateBasis],
  );
  return (
    <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
             data-testid="order-status-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-lg">Order Status</h2>
        <div className="text-[10px] uppercase text-[var(--smoke)] tracking-widest">
          Reporting-only normalization · raw status preserved · Unknown visible
        </div>
      </div>
      {loading && <div className="text-sm">Loading…</div>}
      {err && <div className="text-sm text-red-700">{err}</div>}
      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 text-xs">
            {Object.entries(data.by_group).map(([grp, v]) => (
              <div key={grp} data-testid={`status-group-${grp}`}
                   className="p-2 rounded-lg border-2 border-[var(--ink)] bg-white">
                <div className="uppercase text-[10px] text-[var(--smoke)]">{grp}</div>
                <div className="font-bold">{v.orders} orders</div>
                <div className="font-mono text-[11px]">{fmt(v.customer_total)}</div>
              </div>
            ))}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase text-[var(--smoke)]">
                <th className="pb-1">Status</th>
                <th className="pb-1">Group</th>
                <th className="pb-1 text-right">Orders</th>
                <th className="pb-1 text-right">Customer Total</th>
                <th className="pb-1 text-right">Amount Paid</th>
                <th className="pb-1 text-right">Refunds</th>
                <th className="pb-1 text-right">Outstanding</th>
                <th className="pb-1 text-right">AOV</th>
                <th className="pb-1 text-right">Avg Age (d)</th>
              </tr>
            </thead>
            <tbody>
              {data.by_status.map(r => (
                <tr key={r.status} data-testid={`status-row-${r.status}`}
                    className="border-t border-neutral-100">
                  <td className={`py-1 ${r.group === "Unknown" ? "italic text-yellow-800" : ""}`}>
                    {r.status}
                  </td>
                  <td className="py-1">{r.group}</td>
                  <td className="py-1 text-right font-mono">{r.orders}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.customer_total)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.amount_paid)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.refund_total)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.outstanding_balance)}</td>
                  <td className="py-1 text-right font-mono">{fmt(r.avg_order_value)}</td>
                  <td className="py-1 text-right font-mono">{r.avg_age_days ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

// ── Exports panel (all 10) ───────────────────────────────────────
const EXPORTS = [
  ["sales-summary",     "Sales Summary"],
  ["ledger",            "Full Sales Ledger"],
  ["tax",               "Tax Detail"],
  ["payment-methods",   "Payment Methods"],
  ["fulfillment",       "Fulfillment Methods"],
  ["lead-sources",      "Lead Sources (first-touch)"],
  ["products",          "Product Sales"],
  ["refunds",           "Refunds & Adjustments"],
  ["balances",          "Outstanding Balances"],
  ["order-status",      "Order Status"],
];

export function ExportsPanel({ preset, dateBasis }) {
  return (
    <section className="rounded-2xl border-2 border-[var(--ink)] bg-white p-4 space-y-3"
             data-testid="exports-panel">
      <h2 className="font-display text-lg">CSV Exports</h2>
      <div className="text-[10px] uppercase text-[var(--smoke)] tracking-widest">
        Server-side · admin-only · formula-injection hardened · America/New_York · preview environment
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
        {EXPORTS.map(([key, label]) => (
          <a key={key}
             data-testid={`export-btn-${key}`}
             href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/sales/csv/${key}?preset=${preset}&date_basis=${dateBasis}`}
             className="p-3 rounded-xl border-2 border-[var(--ink)] hover:bg-neutral-50 flex items-center justify-between">
            <span className="text-sm font-bold">{label}</span>
            <span className="uppercase text-[10px] text-[var(--smoke)]">.csv</span>
          </a>
        ))}
      </div>
      <div className="text-xs text-[var(--smoke)] italic">
        Every export ends with a RECONCILIATION row so downstream accounting can verify totals against the on-screen reports.
      </div>
    </section>
  );
}
